// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Colour-by for the Ground and the strip swatches, as token *names*
 * (`'--mat-tegl'`, `'--circ-genbrug'`, …), never values: the viewport reads
 * the computed value of the name (so a theme switch recolours), CSS wraps it
 * in `var()`. `null` means "no colour" — the point is ghosted toward
 * `--color-ghost` and the legend shows a faded ghost swatch.
 *
 * Lenses (Ressourcer's top bar): `mat` materiale — each type in its own
 * colour (`bygning/typeColors`: chosen in the inspector, else automatic),
 * `hier` affaldshierarki, `rev` status. Gennemgang colours by review status, Miljø by the linked
 * samples' stage, Overblik by hierarchy. Pure: no React, no DOM.
 */

import { TREATMENTS, type Sample, type SampleStage, type SurveyType } from '../api/types';
import { TREATMENT_LABEL } from '../kortlaegning/vocab';
import type { ModeId } from './modes';
import { typeColorToken } from './typeColors';
import { labelColorIndex, slotColor, type LabelPalette } from '../viewport/labelColors';
import type { Lens } from './selection';

export type MaterialKey =
  | 'tegl'
  | 'beton'
  | 'letbeton'
  | 'gips'
  | 'trae'
  | 'glas'
  | 'metal'
  | 'bitumen'
  | 'plast'
  | 'mineraluld'
  | 'andet';

/** Legend order. */
export const MATERIALS: readonly MaterialKey[] = [
  'tegl',
  'beton',
  'letbeton',
  'gips',
  'trae',
  'glas',
  'metal',
  'bitumen',
  'plast',
  'mineraluld',
  'andet',
];

export const MATERIAL_LABEL: Record<MaterialKey, string> = {
  tegl: 'Tegl',
  beton: 'Beton',
  letbeton: 'Letbeton',
  gips: 'Gips',
  trae: 'Træ',
  glas: 'Glas',
  metal: 'Metal',
  bitumen: 'Bitumen',
  plast: 'Plast',
  mineraluld: 'Mineraluld',
  andet: 'Andet',
};

export function materialToken(m: MaterialKey): string {
  return `--mat-${m}`;
}

/**
 * Name keywords, most specific first: "Vindue, træ/alu" is glas (the window),
 * not træ; "Letbetonvæg" is letbeton, not beton; "Betonmur" is beton. `exact` keywords must be a
 * whole word ("eg", "alu"), the others may be part of one ("teglsten").
 */
const NAME_RULES: readonly { m: MaterialKey; contains?: readonly string[]; exact?: readonly string[] }[] = [
  { m: 'letbeton', contains: ['letbeton', 'gasbeton', 'porebeton', 'leca', 'letklinker'] },
  { m: 'mineraluld', contains: ['mineraluld', 'isolering', 'rockwool', 'stenuld', 'glasuld'] },
  { m: 'bitumen', contains: ['tagpap', 'bitumen', 'asfalt', 'pap'] },
  { m: 'glas', contains: ['vindue', 'glas', 'rude', 'ovenlys'] },
  { m: 'gips', contains: ['gips'] },
  // Beton before tegl: "Betonmur" is beton; a bare "mur" is read as tegl.
  { m: 'beton', contains: ['beton', 'cement'] },
  { m: 'tegl', contains: ['tegl', 'mursten', 'murværk', 'klinker', 'mur'] },
  {
    m: 'metal',
    contains: ['stål', 'staal', 'metal', 'aluminium', 'kobber', 'zink', 'jern', 'radiator', 'blik'],
    exact: ['alu'],
  },
  { m: 'trae', contains: ['træ', 'trae', 'planke', 'bjælke', 'spær', 'parket', 'krydsfiner', 'dør'], exact: ['eg', 'fyr', 'gran', 'bøg'] },
  { m: 'plast', contains: ['plast', 'pvc', 'linoleum', 'vinyl', 'akryl'] },
];

/** EAK (European waste list) families → material, for a type whose name says nothing. */
const EAK_RULES: readonly { prefix: string; m: MaterialKey }[] = [
  { prefix: '17.01.01', m: 'beton' },
  { prefix: '17.01.02', m: 'tegl' },
  { prefix: '17.01.03', m: 'tegl' },
  { prefix: '17.02.01', m: 'trae' },
  { prefix: '17.02.02', m: 'glas' },
  { prefix: '17.02.03', m: 'plast' },
  { prefix: '17.03', m: 'bitumen' },
  { prefix: '17.04', m: 'metal' },
  { prefix: '17.06.01', m: 'mineraluld' },
  { prefix: '17.06.03', m: 'mineraluld' },
  { prefix: '17.06.04', m: 'mineraluld' },
  { prefix: '17.08', m: 'gips' },
];

function words(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** The material family of a type: its name first, then its EAK code, else `andet`. */
export function materialOf(type: Pick<SurveyType, 'name' | 'eak_code'>): MaterialKey {
  const w = words(type.name);
  for (const rule of NAME_RULES) {
    if (rule.exact?.some((k) => w.includes(k))) return rule.m;
    if (rule.contains?.some((k) => w.some((x) => x.includes(k)))) return rule.m;
  }
  const eak = type.eak_code.trim();
  if (eak) {
    const hit = EAK_RULES.find((r) => eak.startsWith(r.prefix));
    if (hit) return hit.m;
  }
  return 'andet';
}

/** Review status → token; a type still in the queue has no colour (ghost). */
export function reviewToken(type: Pick<SurveyType, 'review_status' | 'environment_status'>): string | null {
  if (type.review_status === 'approved') return '--color-status-succeeded';
  if (type.review_status === 'rejected') return '--color-status-failed';
  if (type.environment_status === 'afventer') return '--tone-warn-ink';
  return null;
}

/** A sample's stage (and result) → token. */
export function sampleToken(sample: Pick<Sample, 'stage' | 'result'>): string {
  if (sample.stage === 'svar') return sample.result === 'forurenet' ? '--color-status-failed' : '--color-status-succeeded';
  if (sample.stage === 'sendt') return '--tone-warn-ink';
  return '--color-status-queued';
}

const STAGE_ORDER: Record<SampleStage, number> = { planlagt: 0, udtaget: 1, sendt: 2, svar: 3 };

/**
 * A type coloured by its samples (Miljø): a forurenet answer wins, then the
 * least advanced open sample; no linked sample = no colour.
 */
export function typeSampleToken(type: Pick<SurveyType, 'id' | 'sample_ids'>, samples: readonly Sample[]): string | null {
  const linked = samples.filter((s) => s.type_ids.includes(type.id) || type.sample_ids.includes(s.id));
  if (linked.length === 0) return null;
  if (linked.some((s) => s.stage === 'svar' && s.result === 'forurenet')) return '--color-status-failed';
  const open = linked.filter((s) => s.stage !== 'svar');
  if (open.length === 0) return '--color-status-succeeded';
  const least = open.reduce((a, b) => (STAGE_ORDER[b.stage] < STAGE_ORDER[a.stage] ? b : a));
  return sampleToken(least);
}

/** The colour of a type under a Ressourcer lens. */
export function lensToken(type: SurveyType, lens: Lens): string | null {
  if (lens === 'mat') return typeColorToken(type);
  if (lens === 'hier') return `--circ-${type.treatment}`;
  return reviewToken(type);
}

/** The colour of a type in a mode (the Ground's type colours). Null = ghost. */
export function modeToken(type: SurveyType, mode: ModeId, lens: Lens, samples: readonly Sample[]): string | null {
  switch (mode) {
    case 'ressourcer':
      return lensToken(type, lens);
    case 'gennemgang':
      return reviewToken(type);
    case 'miljoe':
      return typeSampleToken(type, samples);
    case 'overblik':
      return `--circ-${type.treatment}`;
    default:
      return null;
  }
}

/** One legend row: a token (null = the ghost swatch) and its label. */
export interface LegendItem {
  token: string | null;
  label: string;
  /** A resolved CSS colour, instead of a token: a label cloud's palette slot (`labelLegendItems`). */
  color?: string;
  /** A text-only row ("+ 4 typer mere"): no swatch. */
  plain?: boolean;
  /** Outside Ressourcer's filtered set: shown faded (its parts are ghosted in the model). */
  dim?: boolean;
}

/** The Materiale legend lists at most this many types, then "+ N typer mere". */
export const TYPE_LEGEND_MAX = 10;

/** Which legend a mode shows: a lens, or Miljø's sample stages. */
export type LegendKind = Lens | 'samples';

/** The caption over each legend. */
export const LEGEND_TITLE: Record<LegendKind, string> = {
  mat: 'Materiale',
  hier: 'Affaldshierarki',
  rev: 'Status',
  samples: 'Prøvestatus',
};

/**
 * The legend the Ground shows over the model in a mode: Ressourcer's lens,
 * Gennemgang's review status, Miljø's sample stages. None in Overblik (its
 * hierarchy bars already name the colours) or Rapport (quiet).
 */
export function legendKind(mode: ModeId, lens: Lens): LegendKind | null {
  switch (mode) {
    case 'ressourcer':
      return lens;
    case 'gennemgang':
      return 'rev';
    case 'miljoe':
      return 'samples';
    default:
      return null;
  }
}

/**
 * The legend rows for Etiket: one per label id of the cloud, coloured by the
 * slot the renderer gives it, capped like the Materiale legend.
 */
export function labelLegendItems(labels: Readonly<Record<string, string>>, palette: LabelPalette): LegendItem[] {
  const entries = Object.entries(labels)
    .map(([id, name]) => ({ id: Number(id), name }))
    .filter((e) => Number.isFinite(e.id) && e.id >= 1)
    .sort((a, b) => a.id - b.id);
  const rows: LegendItem[] = entries.slice(0, TYPE_LEGEND_MAX).map((e) => ({
    token: null,
    color: slotColor(palette, labelColorIndex(e.id, palette.colors.length)),
    label: e.name,
  }));
  const rest = entries.length - rows.length;
  if (rest > 0) rows.push({ token: null, label: `+ ${rest} mere`, plain: true });
  return rows;
}

/**
 * The legend rows for a kind. With `types`, the materiale legend lists only
 * the materials present (in legend order), so it never explains a colour the
 * building does not show. With `inSet` (Ressourcer's filtered set, by type
 * id), the set's types come first and a materiale row outside it is `dim`.
 */
export function legendItems(kind: LegendKind, types?: readonly SurveyType[], inSet?: ReadonlySet<number> | null): LegendItem[] {
  switch (kind) {
    case 'mat': {
      // One colour per type: the legend names the types (in the set first, then heaviest first),
      // not material families — so the cap never hides a type the filters kept behind "+ N typer mere".
      const live = (types ?? []).filter((t) => t.review_status !== 'rejected');
      const outside = (t: SurveyType) => (inSet && !inSet.has(t.id) ? 1 : 0);
      const ordered = [...live].sort(
        (a, b) => outside(a) - outside(b) || (b.mass_t ?? -1) - (a.mass_t ?? -1) || a.id - b.id,
      );
      const rows: LegendItem[] = ordered.slice(0, TYPE_LEGEND_MAX).map((t) => {
        const row: LegendItem = { token: typeColorToken(t), label: t.name };
        if (inSet && !inSet.has(t.id)) row.dim = true;
        return row;
      });
      const rest = ordered.length - rows.length;
      if (rest > 0) rows.push({ token: null, label: `+ ${rest} ${rest === 1 ? 'type' : 'typer'} mere`, plain: true });
      return rows;
    }
    case 'hier':
      return TREATMENTS.map((t) => ({ token: `--circ-${t}`, label: TREATMENT_LABEL[t] }));
    case 'rev':
      return [
        { token: '--color-status-succeeded', label: 'Godkendt' },
        { token: '--tone-warn-ink', label: 'Afventer prøve' },
        { token: '--color-status-failed', label: 'Afvist' },
        { token: null, label: 'I kø' },
      ];
    case 'samples':
      return [
        { token: '--color-status-queued', label: 'Planlagt / udtaget' },
        { token: '--tone-warn-ink', label: 'Sendt til lab' },
        { token: '--color-status-succeeded', label: 'Svar: ren' },
        { token: '--color-status-failed', label: 'Svar: forurenet' },
        { token: null, label: 'Ingen prøve' },
      ];
  }
}

/** `var(--x)` for a token name, or the ghost colour for none. */
export function tokenVar(token: string | null): string {
  return `var(${token ?? '--color-ghost'})`;
}
